import React, { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, PlayCircle } from 'lucide-react';
import { getModuleDemoVideo } from '../data/moduleDemoVideos';

/**
 * Collapsible walkthrough video for a module page or Platform Guide section.
 * Starts collapsed so it doesn't dominate the first viewport; expands in place.
 */
export default function ModuleDemoVideo({
  moduleKey,
  defaultOpen = false,
  className = '',
  compact = false,
}) {
  const demo = getModuleDemoVideo(moduleKey);
  const [open, setOpen] = useState(Boolean(defaultOpen));
  const videoRef = useRef(null);
  const panelId = useId();

  useEffect(() => {
    if (!open && videoRef.current) {
      try {
        videoRef.current.pause();
      } catch {
        /* ignore */
      }
    }
  }, [open]);

  if (!demo?.src) return null;

  return (
    <div
      className={`rounded-xl border border-[#fbbf0f]/25 bg-[#16130d]/80 ${className}`}
      data-testid={`module-demo-${moduleKey}`}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.03] transition-colors rounded-xl"
        data-testid={`module-demo-toggle-${moduleKey}`}
      >
        <PlayCircle className="w-5 h-5 text-[#fbbf0f] shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white truncate">
            {compact ? 'Watch demo' : demo.title}
          </p>
          <p className="text-[11px] text-[#c4b8a5]">
            {demo.durationLabel || '~2 min'} · see the real workflow before you start
          </p>
        </div>
        {open
          ? <ChevronUp className="w-4 h-4 text-[#c4b8a5] shrink-0" />
          : <ChevronDown className="w-4 h-4 text-[#c4b8a5] shrink-0" />}
      </button>

      {open && (
        <div id={panelId} className="px-3 pb-3">
          <div className="overflow-hidden rounded-lg border border-white/10 bg-black">
            <video
              ref={videoRef}
              controls
              playsInline
              preload="metadata"
              className="block w-full aspect-video bg-black"
              src={demo.src}
              data-testid={`module-demo-player-${moduleKey}`}
            >
              Your browser does not support embedded video.
            </video>
          </div>
        </div>
      )}
    </div>
  );
}
